import * as Sentry from "@sentry/react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import { BUILD_INFO } from "../generatedBuildInfo";

const ERROR_LOG_COLLECTION = "clientErrorLogs";
const MAX_FIELD_LENGTH = 700;

const redactPatterns = [
  /sk-[a-zA-Z0-9_-]+/g,
  /Bearer\s+[a-zA-Z0-9._-]+/gi,
  /AIza[a-zA-Z0-9_-]+/g,
  /password["']?\s*[:=]\s*["']?[^"',\s]+/gi,
  /token["']?\s*[:=]\s*["']?[^"',\s]+/gi,
];

const redact = (value = "") => {
  let safe = String(value || "");
  redactPatterns.forEach((pattern) => {
    safe = safe.replace(pattern, "[redacted]");
  });
  return safe.slice(0, MAX_FIELD_LENGTH);
};

const getRoute = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname || ""}${window.location.search || ""}`;
};

const normalizeError = (error) => {
  if (!error) return { technicalMessage: "Unknown error", stack: "" };
  if (error instanceof Error) {
    return {
      technicalMessage: redact(error.message),
      stack: redact(error.stack || ""),
      code: redact(error.code || ""),
      name: redact(error.name || "Error"),
    };
  }
  return {
    technicalMessage: redact(typeof error === "string" ? error : JSON.stringify(error)),
    stack: "",
    code: "",
    name: "NonError",
  };
};

export const buildLogPayload = ({
  error,
  type = "client_error",
  module = "unknown",
  userMessage = "Something went wrong. Please try again.",
  accountType = "",
  extra = {},
} = {}) => {
  const normalized = normalizeError(error);
  const user = auth.currentUser;

  return {
    type: redact(type),
    module: redact(module),
    userMessage: redact(userMessage),
    technicalMessage: normalized.technicalMessage,
    stack: normalized.stack,
    code: normalized.code,
    name: normalized.name,
    route: redact(getRoute()),
    userId: user?.uid || "",
    userEmailDomain: user?.email ? String(user.email).split("@").pop() : "",
    accountType: redact(accountType || ""),
    appVersion: BUILD_INFO.version || "unknown",
    buildTimestamp: BUILD_INFO.buildTimestamp || "",
    timestampIso: new Date().toISOString(),
    extra: Object.fromEntries(
      Object.entries(extra || {}).map(([key, value]) => [
        redact(key),
        redact(typeof value === "string" ? value : JSON.stringify(value ?? "")),
      ])
    ),
  };
};

export const logAppError = async (options = {}) => {
  const payload = buildLogPayload(options);

  try {
    Sentry.captureException(options.error || new Error(payload.technicalMessage), {
      tags: {
        module: payload.module,
        type: payload.type,
        route: payload.route,
      },
      extra: payload,
      user: payload.userId ? { id: payload.userId } : undefined,
    });
  } catch (_) {
    // Logging must never break the user workflow.
  }

  try {
    if (payload.userId) {
      await addDoc(collection(db, ERROR_LOG_COLLECTION), {
        ...payload,
        timestamp: serverTimestamp(),
      });
    }
  } catch (firestoreError) {
    try {
      console.warn("Client error log could not be persisted:", firestoreError);
    } catch (_) {
      // no-op
    }
  }

  return payload;
};

export const logAppEvent = (message, extra = {}) => {
  try {
    Sentry.captureMessage(redact(message), { extra });
  } catch (_) {
    // no-op
  }
};

